import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useUTMContext } from "@src/context/UTMContext";
import logger from "@src/utils/logger";
import { appendParamsToUrl } from "@src/utils/url-utils";
import { useRouter } from "@uhg-abyss/web/hooks/useRouter";
import {
  type NavigateOptions,
  useInRouterContext,
  useLocation,
} from "@uhg-abyss/web/tools/reactRouterTools";
import { useCallback } from "react";

type NavigateWithUTMOptions = Pick<NavigateOptions, "replace" | "state">;

export const useNavigateWithUTM = () => {
  const inRouterContext = useInRouterContext();
  const { navigate } = useRouter();
  const { utmTags } = useUTMContext();
  const { beginTransition } = useInteractionGuard();
  const location = useLocation();

  const navigateWithUTM = useCallback(
    (to: string, options?: NavigateWithUTMOptions) => {
      const urlWithParams = appendParamsToUrl(
        to,
        (utmTags as Record<string, string | undefined>) || {},
      );

      if (!inRouterContext) {
        logger.warn({
          text: "useNavigateWithUTM called outside Router context; navigation skipped.",
        });
        return;
      }

      // Skip beginTransition for same-route navigations. RouteChangeListener
      // only fires when the location key changes, so a no-op navigate (e.g.
      // "Book again" on /provider-profile) would start a transition lock that
      // endTransition never clears, leaving the UI blocked for 10 seconds.
      const destinationPathname = to.split("?")[0].split("#")[0];
      if (destinationPathname !== location.pathname) {
        beginTransition(urlWithParams);
      }

      navigate(urlWithParams, options);
    },
    [inRouterContext, navigate, utmTags, beginTransition, location.pathname],
  );

  return navigateWithUTM;
};
